/**
 * Play Store asset downloads — public page (no auth) with anchor tags
 * that force the browser to download files rather than navigate to them
 * via the SPA router. Accessed at /downloads.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Download, ArrowLeft, FileArchive, Image as ImageIcon, FileText } from "lucide-react";

const FILES = [
  {
    group: "Android setup helpers",
    note: "If launcher icons keep showing the old logo: drop fix-android-icons.bat into your project root (the folder with android/) and double-click it. Auto-replaces icons + deletes XML overrides.",
    items: [
      { name: "fix-android-icons.bat (one-click icon fix)", url: "/fix-android-icons.bat", kind: "text" },
      { name: "PLAY_CONSOLE_STEP_BY_STEP.md (full guide)", url: "/PLAY_CONSOLE_STEP_BY_STEP.md", kind: "text" },
    ],
  },
  {
    group: "Android launcher icons",
    note: "Manual route — unzip, drop the 5 mipmap-* folders into android/app/src/main/res/, then DELETE android/app/src/main/res/mipmap-anydpi-v26/ic_launcher.xml + .xml round.",
    items: [
      { name: "fitjong-android-icons.zip", url: "/fitjong-android-icons.zip", kind: "zip" },
    ],
  },
  {
    group: "Play Store listing assets",
    note: "Feature graphic + 4 phone screenshots (1080×1920) for the Play Console listing",
    items: [
      { name: "Everything (zip)", url: "/fitjong-play-store-assets.zip", kind: "zip" },
      { name: "feature_graphic.png (1024×500)", url: "/play-store-assets/feature_graphic.png", kind: "image" },
      { name: "screenshot_1.png", url: "/play-store-assets/screenshot_1.png", kind: "image" },
      { name: "screenshot_2.png", url: "/play-store-assets/screenshot_2.png", kind: "image" },
      { name: "screenshot_3.png", url: "/play-store-assets/screenshot_3.png", kind: "image" },
      { name: "screenshot_4.png", url: "/play-store-assets/screenshot_4.png", kind: "image" },
      { name: "listing-copy.txt (full description)", url: "/play-store-assets/listing-copy.txt", kind: "text" },
    ],
  },
  {
    group: "Brand assets",
    note: "Source logo + the 512×512 app icon Play Console asks for",
    items: [
      { name: "logo.png (1254×1254 master)", url: "/logo.png", kind: "image" },
      { name: "icon-512.png (Play Store app icon)", url: "/icon-512.png", kind: "image" },
      { name: "icon-192.png (Android home screen)", url: "/icon-192.png", kind: "image" },
    ],
  },
];

const ICONS = { zip: FileArchive, image: ImageIcon, text: FileText };

export default function Downloads() {
  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2C332A] px-4 py-8 md:py-12">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#2C332A]/60 hover:text-[#2C332A] mb-6">
          <ArrowLeft size={16} strokeWidth={1.8} /> Back to home
        </Link>
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="Fitjong" className="w-12 h-12 rounded-xl shadow-sm" />
          <div>
            <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-3xl font-light leading-tight">
              Fitjong assets
            </h1>
            <p className="text-sm text-[#2C332A]/60">Tap any button to save the file to your phone / PC.</p>
          </div>
        </div>

        <div className="space-y-6">
          {FILES.map((group) => (
            <section key={group.group} className="bg-white border border-[#E6E2D8] rounded-2xl p-5 shadow-sm">
              <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-lg font-medium mb-1">
                {group.group}
              </h2>
              <p className="text-xs text-[#2C332A]/60 mb-4">{group.note}</p>
              <div className="flex flex-col gap-2">
                {group.items.map((item) => {
                  const Icon = ICONS[item.kind] || Download;
                  return (
                    <a
                      key={item.url}
                      href={item.url}
                      download
                      data-testid={`download-${item.url.split("/").pop()}`}
                      className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#F7F5F0] hover:bg-[#EFEBE0] border border-[#E6E2D8] transition-colors group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Icon size={18} strokeWidth={1.8} className="text-[#5F7161] shrink-0" />
                        <span className="text-sm font-medium truncate">{item.name}</span>
                      </div>
                      <Download
                        size={16}
                        strokeWidth={1.8}
                        className="text-[#2C332A]/40 group-hover:text-[#5F7161] shrink-0"
                      />
                    </a>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <p className="text-xs text-[#2C332A]/50 mt-8 leading-relaxed">
          If you tap a file and it still opens the game instead of downloading, your browser is caching a previous
          redirect. Open this page in a fresh tab or hold-tap the button and choose "Download link" / "Save link as…".
        </p>
      </div>
    </div>
  );
}
